/**
 * ReceiptForm — Phase 96
 *
 * Renders for document types where is_payment_document = true (e.g. קבלה RC).
 * Shows payment-method rows (cash / check / credit / bank transfer) instead of
 * product line items. After saving, offers an allocation drawer to close open
 * invoices for the selected client.
 */

import { useEffect, useState, useMemo } from 'react';
import { Plus, Trash2, Loader as Loader2, CircleAlert as AlertCircle, Calendar, Users, Hash, Banknote, CreditCard, Building2, Wallet, Circle as HelpCircle, CircleCheck as CheckCircle2, Link as LinkIcon, TriangleAlert as AlertTriangle, X } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { supabase } from '@/lib/supabase';
import type { InventoryPrintData } from '@/components/inventory/InventoryDocumentPrintView';

// ─── Types ────────────────────────────────────────────────────────────────────

type PaymentMethod = 'cash' | 'check' | 'credit_card' | 'bank_transfer' | 'other';

interface PaymentLine {
  payment_method:   PaymentMethod;
  amount:           string;
  payment_date:     string;
  reference_number: string;
  bank_name:        string;
  notes:            string;
}

interface Client {
  id:            string;
  name:          string;
  is_vat_liable: boolean;
}

interface OpenInvoice {
  id:                 string;
  document_number:    string;
  doc_type_name:      string;
  doc_type_prefix:    string;
  issue_date:         string;
  total:              number;
  outstanding_amount: number;
  days_open:          number;
}

interface DocumentType {
  id:                string;
  name:              string;
  next_number:       number;
  prefix:            string;
  requires_receipt:  boolean;
  is_vat_liable:     boolean;
  updates_inventory: boolean;
  has_line_discount: boolean;
  is_payment_document: boolean;
}

interface Props {
  docType:              DocumentType;
  companyId:            string;
  vatRate:              number;
  showToast:            (msg: string, ok?: boolean) => void;
  onSuccess:            (docTypeName: string, docRef: string, printData: InventoryPrintData) => void;
  onBeforeFirstSubmit?: () => Promise<boolean>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash',          label: 'מזומן'          },
  { value: 'check',         label: 'צ׳ק'             },
  { value: 'credit_card',   label: 'אשראי'           },
  { value: 'bank_transfer', label: 'העברה בנקאית'    },
  { value: 'other',         label: 'אחר'             },
];

const METHOD_COLORS: Record<PaymentMethod, string> = {
  cash:          'text-emerald-700 bg-emerald-50 border-emerald-300 ring-emerald-400',
  check:         'text-blue-700    bg-blue-50    border-blue-300    ring-blue-400',
  credit_card:   'text-sky-700     bg-sky-50     border-sky-300     ring-sky-400',
  bank_transfer: 'text-gray-700    bg-gray-50    border-gray-300    ring-gray-400',
  other:         'text-gray-500    bg-gray-50    border-gray-200    ring-gray-300',
};

const METHOD_UNSELECTED = 'text-gray-400 bg-white border-gray-200 hover:bg-gray-50';

const EMPTY_LINE: PaymentLine = {
  payment_method:   'cash',
  amount:           '',
  payment_date:     today(),
  reference_number: '',
  bank_name:        '',
  notes:            '',
};

function parseNum(s: string): number {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function fmtCurrency(n: number): string {
  return `₪${Math.abs(n).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Allocation Drawer ────────────────────────────────────────────────────────

interface AllocationRow {
  invoice: OpenInvoice;
  amount:  string;
  checked: boolean;
}

interface AllocationDrawerProps {
  receiptTotal:  number;
  companyId:     string;
  clientId:      string;
  receiptDocId:  string;
  onDone:        () => void;
}

function AllocationDrawer({ receiptTotal, companyId, clientId, receiptDocId, onDone }: AllocationDrawerProps) {
  const [rows,    setRows]    = useState<AllocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    supabase.rpc('get_client_open_invoices', {
      p_company_id: companyId,
      p_client_id:  clientId,
    }).then(({ data, error: e }) => {
      if (e) { setError(e.message); }
      else {
        setRows(((data ?? []) as OpenInvoice[]).map(inv => ({
          invoice: inv,
          amount:  '',
          checked: false,
        })));
      }
      setLoading(false);
    });
  }, [companyId, clientId]);

  const allocated = useMemo(
    () => rows.filter(r => r.checked).reduce((s, r) => s + parseNum(r.amount), 0),
    [rows],
  );
  const remaining = receiptTotal - allocated;
  const balanced  = Math.abs(remaining) < 0.01;

  const toggle = (idx: number) =>
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const checked = !r.checked;
      const amount  = checked && !r.amount
        ? Math.min(r.invoice.outstanding_amount, receiptTotal).toFixed(2)
        : r.amount;
      return { ...r, checked, amount };
    }));

  const updateAmt = (idx: number, val: string) =>
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, amount: val } : r));

  const handleSave = async () => {
    const selected = rows.filter(r => r.checked && parseNum(r.amount) > 0);
    if (selected.length === 0) { onDone(); return; }
    if (allocated > receiptTotal + 0.01) { setError('סכום ההקצאות עולה על סכום הקבלה'); return; }
    for (const r of selected) {
      if (parseNum(r.amount) > r.invoice.outstanding_amount + 0.01) {
        setError(`סכום עולה על יתרה של ${r.invoice.document_number}`);
        return;
      }
    }
    setSaving(true); setError(null);
    const { error: e } = await supabase.rpc('allocate_receipt_to_invoices', {
      p_receipt_id:  receiptDocId,
      p_allocations: selected.map(r => ({
        invoice_document_id: r.invoice.id,
        allocated_amount:    parseNum(r.amount),
      })),
    });
    setSaving(false);
    if (e) { setError(e.message); return; }
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <LinkIcon className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">שיוך קבלה לחשבוניות</h3>
              <p className="text-xs text-gray-400">קבלה: {fmtCurrency(receiptTotal)}</p>
            </div>
          </div>
          <button onClick={onDone} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">טוען חשבוניות פתוחות…</span>
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="flex flex-col items-center py-10 gap-2 text-gray-400">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              <p className="text-sm">אין חשבוניות פתוחות ללקוח זה</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <>
              <p className="text-xs text-gray-500">סמן חשבוניות לסגירה ותקן סכום לשיוך חלקי:</p>
              <div className="space-y-2">
                {rows.map((row, idx) => (
                  <div key={row.invoice.id}
                       className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors
                         ${row.checked ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={row.checked} onChange={() => toggle(idx)}
                           className="w-4 h-4 rounded accent-blue-600" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-gray-800">{row.invoice.document_number}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium
                          ${row.invoice.days_open > 60 ? 'bg-red-100 text-red-700'
                            : row.invoice.days_open > 30 ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-600'}`}>
                          {row.invoice.days_open} ימים
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {new Date(row.invoice.issue_date).toLocaleDateString('he-IL')} · פתוח: {fmtCurrency(row.invoice.outstanding_amount)}
                      </p>
                    </div>
                    {row.checked ? (
                      <div className="relative w-28">
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₪</span>
                        <input type="number" min="0.01" step="0.01" value={row.amount}
                               onChange={e => updateAmt(idx, e.target.value)}
                               className="w-full pl-2 pr-7 py-1.5 border border-blue-300 rounded-lg text-sm font-mono text-left focus:outline-none focus:ring-2 focus:ring-blue-400"
                               dir="ltr" />
                      </div>
                    ) : (
                      <span className="text-sm font-semibold text-gray-500 w-28 text-left">
                        {fmtCurrency(row.invoice.outstanding_amount)}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Balance */}
              <div className={`flex items-center justify-between px-4 py-3 rounded-xl border
                ${balanced ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2 text-sm">
                  {balanced
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                  <span className={balanced ? 'text-emerald-700' : 'text-amber-700'}>
                    {balanced ? 'מאוזן' : remaining > 0 ? `נותר: ${fmtCurrency(remaining)}` : `עודף: ${fmtCurrency(-remaining)}`}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {fmtCurrency(allocated)} / {fmtCurrency(receiptTotal)}
                </span>
              </div>
            </>
          )}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onDone}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            דלג (לשייך מאוחר)
          </button>
          <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700
                             disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {rows.filter(r => r.checked).length > 0 ? 'שמור שיוך' : 'סיים'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ReceiptForm({ docType, companyId, showToast, onSuccess, onBeforeFirstSubmit }: Props) {

  // Reference data
  const [clients,    setClients]    = useState<Client[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError,   setRefError]   = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    supabase
      .from('inventory_clients')
      .select('id, name, is_vat_liable')
      .eq('company_id', companyId)
      .order('name')
      .then(({ data, error }) => {
        if (error) setRefError(error.message);
        else setClients((data ?? []) as Client[]);
        setRefLoading(false);
      });
  }, [companyId]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle()
        .then(({ data }) => { if (data?.full_name) setIssuedByName(data.full_name); });
    });
  }, []);

  // Form state
  const [issueDate,  setIssueDate]  = useState(today());
  const [clientId,   setClientId]   = useState('');
  const [notes,      setNotes]      = useState('');
  const [lines,      setLines]      = useState<PaymentLine[]>([{ ...EMPTY_LINE }]);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [issuedByName, setIssuedByName] = useState('');

  // Allocation state
  const [showAlloc,     setShowAlloc]     = useState(false);
  const [createdDocId,  setCreatedDocId]  = useState<string | null>(null);
  const [createdDocRef, setCreatedDocRef] = useState<string | null>(null);

  const selectedClient = useMemo(() => clients.find(c => c.id === clientId) ?? null, [clients, clientId]);
  const total = useMemo(() => lines.reduce((s, l) => s + parseNum(l.amount), 0), [lines]);

  // Line helpers
  const updateLine = (idx: number, patch: Partial<PaymentLine>) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const addLine = () =>
    setLines(prev => [...prev, { ...EMPTY_LINE, payment_date: issueDate }]);

  const removeLine = (idx: number) =>
    setLines(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  // Validation
  const validate = (): string | null => {
    if (lines.length === 0) return 'יש להוסיף לפחות שורת תשלום אחת';
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!;
      if (parseNum(l.amount) <= 0) return `שורה ${i + 1}: סכום חייב להיות גדול מאפס`;
      if (!l.payment_date)          return `שורה ${i + 1}: יש לבחור תאריך`;
      if (l.payment_method === 'check' && !l.reference_number)
        return `שורה ${i + 1}: צ'ק דורש מספר`;
    }
    return null;
  };

  // Submit
  const handleSubmit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    if (onBeforeFirstSubmit) {
      const ok = await onBeforeFirstSubmit();
      if (!ok) return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      // Create document with no product lines
      const { data: docId, error: rpcErr } = await supabase.rpc('process_inventory_document', {
        payload: {
          company_id:       companyId,
          document_type_id: docType.id,
          warehouse_id:     null,
          supplier_id:      null,
          client_id:        clientId || null,
          issued_by_name:   issuedByName || null,
          issue_date:       issueDate,
          subtotal:         total,
          vat_amount:       0,
          total,
          notes:            notes.trim() || null,
          lines:            [],
        },
      });
      if (rpcErr) throw rpcErr;

      const docRef = `${docType.prefix || ''}${docType.next_number}`;

      // Save payment lines
      const { error: plErr } = await supabase.rpc('upsert_receipt_payment_lines', {
        p_document_id: docId as string,
        p_lines: lines.map(l => ({
          payment_method:   l.payment_method,
          amount:           parseNum(l.amount),
          payment_date:     l.payment_date || issueDate,
          reference_number: l.reference_number || null,
          bank_name:        l.bank_name || null,
          notes:            l.notes || null,
        })),
      });
      if (plErr) throw plErr;

      showToast(`${docType.name} ${docRef} הופק בהצלחה`, true);
      setCreatedDocId(docId as string);
      setCreatedDocRef(docRef);

      if (clientId && docId) {
        setShowAlloc(true);
      } else {
        onSuccess(docType.name, docRef, buildPrintData(docRef));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message
        : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message)
        : String(e);
      setFormError(msg);
      showToast(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  const buildPrintData = (docRef: string): InventoryPrintData => ({
    docTypeName: docType.name,
    docNumber:   docRef,
    date:        issueDate,
    clientName:  selectedClient?.name ?? undefined,
    lines:       [],
    subtotal:    total,
    vatAmount:   0,
    grandTotal:  total,
    vatRate:     0,
    documentId:  createdDocId ?? undefined,
    clientId:    clientId || undefined,
    issuedByName: issuedByName || undefined,
  });

  const handleAllocDone = () => {
    setShowAlloc(false);
    const ref = createdDocRef ?? `${docType.prefix}${docType.next_number}`;
    onSuccess(docType.name, ref, buildPrintData(ref));
  };

  // ── Guards ──────────────────────────────────────────────────────────────────
  if (refLoading) return (
    <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
      <Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">טוען נתונים…</span>
    </div>
  );
  if (refError) return (
    <div className="flex items-center gap-3 m-6 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
      <AlertCircle className="w-4 h-4 shrink-0" />{refError}
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5" dir="rtl">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-emerald-50 border-b border-emerald-200 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-xl shadow-sm">
            <Banknote className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-emerald-800">{docType.name}</h2>
            <p className="text-xs text-emerald-500">הזן אמצעי תשלום שהתקבלו</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-emerald-200 shadow-sm">
          <Hash className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-500">מספר הבא:</span>
          <span className="font-mono font-bold text-orange-600 text-sm">
            {(docType.prefix || '') + docType.next_number}
          </span>
        </div>
      </div>

      <div className="px-6 space-y-6 pb-6">

        {formError && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{formError}
          </div>
        )}

        {/* Header fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />תאריך קבלה
            </label>
            <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)}
                   className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                   dir="ltr" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />לקוח
              <span className="text-gray-400 normal-case font-normal">(אופציונלי)</span>
            </label>
            <SearchableSelect
              options={clients.map(c => ({ value: c.id, label: c.name }))}
              value={clientId}
              onChange={setClientId}
              placeholder="— בחר לקוח —"
            />
          </div>
        </div>

        {/* Payment lines */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Banknote className="w-3.5 h-3.5" />אמצעי תשלום
            </p>
            <button type="button" onClick={addLine}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors">
              <Plus className="w-3.5 h-3.5" />הוסף אמצעי תשלום
            </button>
          </div>

          <div className="space-y-3">
            {lines.map((line, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 space-y-4">

                {/* Row 1: method selector */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">אמצעי תשלום</label>
                  <div className="flex gap-2 flex-wrap">
                    {PAYMENT_METHODS.map(m => (
                      <button key={m.value} type="button"
                              onClick={() => updateLine(idx, { payment_method: m.value })}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all
                                ${line.payment_method === m.value
                                  ? METHOD_COLORS[m.value] + ' ring-1 ring-offset-1 shadow-sm'
                                  : METHOD_UNSELECTED}`}>
                        {m.value === 'cash'          && <Banknote   className="w-3.5 h-3.5" />}
                        {m.value === 'check'         && <Wallet     className="w-3.5 h-3.5" />}
                        {m.value === 'credit_card'   && <CreditCard className="w-3.5 h-3.5" />}
                        {m.value === 'bank_transfer' && <Building2  className="w-3.5 h-3.5" />}
                        {m.value === 'other'         && <HelpCircle className="w-3.5 h-3.5" />}
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 2: amount + date + remove */}
                <div className="grid grid-cols-[1fr_10rem_2rem] gap-3 items-end">
                  <div className="space-y-1">
                    <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">סכום</label>
                    <div className="relative">
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">₪</span>
                      <input type="number" min="0.01" step="0.01" value={line.amount}
                             onChange={e => updateLine(idx, { amount: e.target.value })}
                             placeholder="0.00"
                             className="w-full pl-2 pr-7 py-2.5 border border-gray-300 rounded-lg text-sm font-mono text-left focus:outline-none focus:ring-2 focus:ring-emerald-400"
                             dir="ltr" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      {line.payment_method === 'check' ? 'תאריך פירעון' : 'תאריך'}
                    </label>
                    <input type="date" value={line.payment_date}
                           onChange={e => updateLine(idx, { payment_date: e.target.value })}
                           className="w-full px-2.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                           dir="ltr" />
                  </div>
                  <button type="button" onClick={() => removeLine(idx)} disabled={lines.length === 1}
                          className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 disabled:opacity-30 rounded-lg transition-colors self-end">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Row 3: reference / bank (conditional) */}
                {(line.payment_method === 'check' || line.payment_method === 'credit_card' || line.payment_method === 'bank_transfer') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                        {line.payment_method === 'check'         ? 'מספר צ׳ק'
                          : line.payment_method === 'credit_card' ? '4 ספרות אחרונות'
                          : 'מספר אסמכתא'}
                      </label>
                      <input type="text" value={line.reference_number}
                             onChange={e => updateLine(idx, { reference_number: e.target.value })}
                             placeholder={line.payment_method === 'check' ? '12345' : '****1234'}
                             className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
                             dir="ltr" />
                    </div>
                    {line.payment_method === 'check' && (
                      <div className="space-y-1">
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">שם בנק</label>
                        <input type="text" value={line.bank_name}
                               onChange={e => updateLine(idx, { bank_name: e.target.value })}
                               placeholder="לאומי, הפועלים…"
                               className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">הערות</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                    placeholder="הערה לקבלה (אופציונלי)"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400" />
        </div>

        {/* Total */}
        <div className="flex items-center justify-between px-5 py-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <span className="text-sm font-semibold text-emerald-800">סה״כ קבלה</span>
          <span className="text-2xl font-bold text-emerald-700 font-mono">{fmtCurrency(total)}</span>
        </div>

        {clientId && (
          <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700">
            <LinkIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />
            <p>לאחר שמירת הקבלה תוכל לשייך אותה לחשבוניות פתוחות של לקוח זה.</p>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end pt-2">
          <button type="button" onClick={handleSubmit} disabled={submitting || total <= 0}
                  className="flex items-center gap-2.5 px-8 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'שומר…' : `הפק ${docType.name}`}
          </button>
        </div>
      </div>

      {/* Allocation drawer */}
      {showAlloc && createdDocId && clientId && (
        <AllocationDrawer
          receiptTotal={total}
          companyId={companyId}
          clientId={clientId}
          receiptDocId={createdDocId}
          onDone={handleAllocDone}
        />
      )}
    </div>
  );
}
